import type { Health, ProductMetric, Stage } from '../../services/products'
import { STAGE_LABEL } from '../../services/products'
import { fmtValue } from './format'

const STAGE_CLS: Record<Stage, string> = {
  idea: 'bg-muted text-muted-foreground', discovery: 'bg-primary/10 text-primary',
  development: 'bg-primary/15 text-primary', launch: 'bg-warning/15 text-warning',
  growth: 'bg-success/15 text-success', mature: 'bg-success/10 text-foreground', sunset: 'bg-muted text-muted-foreground',
}

export function StageBadge({ stage }: { stage: Stage }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_CLS[stage]}`}>{STAGE_LABEL[stage]}</span>
}

export function HealthBadge({ health, reason }: { health: Health; reason?: string | null }) {
  if (!health) return null
  const map = {
    at_risk: ['At risk', 'bg-destructive/15 text-destructive'],
    watch: ['Watch', 'bg-warning/15 text-warning'],
    on_track: ['On track', 'bg-success/15 text-success'],
  } as const
  const [label, cls] = map[health]
  return <span title={reason ?? undefined} className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>
}

/** Tiny trend line; decorative, the numbers are shown next to it. */
export function Sparkline({ values, good }: { values: number[]; good: boolean | null }) {
  if (values.length < 2) return null
  const w = 96, h = 28
  const min = Math.min(...values), max = Math.max(...values)
  const span = max - min || 1
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - 2 - ((v - min) / span) * (h - 4)}`).join(' ')
  const stroke = good === null ? 'currentColor' : good ? 'var(--success)' : 'var(--warning)'
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" className="text-muted-foreground">
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

export function MetricLine({ m }: { m: ProductMetric }) {
  const delta = m.latest !== null && m.previous !== null ? m.latest - m.previous : null
  const better = delta === null || delta === 0 ? null : (delta > 0) === (m.direction === 'up')
  return (
    <div className="flex items-baseline justify-between gap-2 text-sm">
      <span className="truncate text-muted-foreground">{m.name}</span>
      <span className="shrink-0 font-medium text-foreground">
        {fmtValue(m.latest, m.unit)}
        {delta !== null && delta !== 0 && (
          <span className={`ml-1 text-xs ${better ? 'text-success' : 'text-warning'}`}>
            {delta > 0 ? '▲' : '▼'} {fmtValue(Math.abs(delta), m.unit === '%' ? 'pts' : m.unit)}
          </span>
        )}
      </span>
    </div>
  )
}
